import {
  createUserSchema,
  organizationSchema,
  patientSchema,
  providerSchema,
  siteSchema,
  userSchema,
} from '@billing/validation';
import z from 'zod';
import { DateTime } from 'luxon';

const DEFAULT_SITE_TIMEZONE = 'America/Winnipeg';
export const PERMANENT_PATIENT_ID = '0cd24476-ea44-411f-bded-af38583639c5';
export const PERMANENT_PATIENT_HEALTHCARD_ID =
  '1cd24476-ea44-411f-bded-af38583639c5';

export const permanentOrganization: z.infer<typeof organizationSchema> = {
  id: '9e26288a-b7eb-4d8a-a0a9-f991a13c47bc',
  name: 'Permanent Integration Test Organization',
  address: '123 Fake Street',
  city: 'Winnipeg',
  province: 'MB',
};

export const integrationOrganization: z.infer<typeof organizationSchema> = {
  id: '8f2decd9-6e32-4746-864b-9c9ba5367775',
  name: 'Test Organization',
  address: '123 Fake Street',
  city: 'Winnipeg',
  province: 'MB',
};

export const permanentSite: z.infer<typeof siteSchema> = {
  id: '0dce25e7-ea26-4025-b66b-6a568a049a48',
  name: 'Permanent Integration Test Site',
  address: '123 Fake Street',
  email: 'test@example.com',
  phone: '+12045555555',
  postalCode: 'R1R1R1',
  fax: '+12045555555',
  region: 'Winnipeg',
  status: 'Active',
  description: 'Integration Test Site',
  proFees: false,
  techFees: false,
  organizationID: permanentOrganization.id,
  // organization: permanentOrganization,
  siteNumber: '12345',
  hospitalCode: '12345',
  city: 'Winnipeg',
  province: 'MB',
  overhead: 15.0,
  invoicee: 'site' as 'site' | 'provider',
  invoiceRate: 25.0,
  apiID: null,
  apiSecret: null,
  apiKey: null,
  autoRemit: false,
  cheque: false,
  creditCard: false,
  etransfer: false,
  requiresTariffIcd9: true,
  creditCardPhoneNumber: null,
  etransferEmail: null,
  etransferPasswordRequirement: null,
  timezone: DEFAULT_SITE_TIMEZONE,
  allowUploads: true,
  settings: { defaultPayorID: null },
};

export const integrationSite: z.infer<typeof siteSchema> = {
  id: 'fe578cc0-3db6-43b8-aca3-b91814aab2db',
  name: 'Integration Test Site',
  address: '123 Fake Street',
  email: 'test@example.com',
  phone: '+12045555555',
  postalCode: 'R1R1R1',
  fax: '+12045555555',
  region: 'Winnipeg',
  status: 'Active',
  description: 'Integration Test Site',
  proFees: false,
  techFees: false,
  organizationID: permanentOrganization.id,
  siteNumber: '12345',
  city: 'Winnipeg',
  province: 'MB',
  overhead: 15,
  invoicee: 'site',
  invoiceRate: 25,
  apiID: 'api-id-123',
  apiKey: 'api-key-123',
  apiSecret: 'api-secret-123',
  // organization: permanentOrganization,
  hospitalCode: '12345',
  autoRemit: false,
  cheque: false,
  creditCard: false,
  etransfer: false,
  requiresTariffIcd9: true,
  creditCardPhoneNumber: null,
  etransferEmail: null,
  etransferPasswordRequirement: null,
  timezone: DEFAULT_SITE_TIMEZONE,
  allowUploads: true,
  settings: { defaultPayorID: null },
};

export const permanentUser: z.infer<typeof userSchema> = {
  id: 'ead48e53-8161-489b-8f85-f7457a031a44',
  firstName: 'Integration',
  lastName: 'Test User',
  email: 'billingtestuser@gmail.com',
  status: 'active',
  phone: '+12045555555',
  organizationID: permanentOrganization.id,
  roleID: 'c159e7ff-3c56-495e-be74-d38ef0cfcf93',
  providerID: null,
  cognitoID: '',
  providers: [],
  organization: null,
  role: null,
};

export const integrationTestUser = {
  id: '9418ea45-4800-4c17-9675-f4900b9e2fa1',
  firstName: 'Integration',
  lastName: 'Test User',
  phone: '+12045555555',
  email: 'userbillingtest@gmail.com',
  status: 'active',
  cognitoID: '4cbd05b8-c0c1-7052-32a2-ddee481e953d',
  organizationID: permanentOrganization.id,
  roleID: 'c159e7ff-3c56-495e-be74-d38ef0cfcf93',
  providerID: null,
  organization: permanentOrganization,
  role: {
    id: 'c159e7ff-3c56-495e-be74-d38ef0cfcf93',
    name: 'Integration Test User',
    organizationID: permanentOrganization.id,
    organization: permanentOrganization,
    permissions: [
      { id: '4e1ae27c-271b-4325-bc26-511f62dbf97f', access: 'submissions' },
      { id: '2827ffa4-9368-44a8-96c6-f1db6898bef0', access: 'users' },
      { id: '44ead7a7-6e7c-46d8-a88d-26e2ee97737a', access: 'reports' },
      { id: '0b0ea386-8e85-4dde-946e-b013812e5391', access: 'sites' },
      { id: 'deb3c865-282a-4ac2-bb50-ec32f9539bf3', access: 'claims' },
      { id: '6f770a35-4ffd-423d-8d6a-a47e7d4c7780', access: 'providers' },
      { id: '1414fd99-cfae-49dd-9382-00fcfab731b5', access: 'remittances' },
      { id: 'a7b14d62-4243-4f03-b9b9-5edcc5240df7', access: 'credits' },
      { id: '0141fe2e-2eb6-4735-b889-dfe5ec1f6a3f', access: 'transactions' },
      {
        id: '063019da-9a83-4632-a105-710f5632ac0b',
        access: 'organizations',
      },
      { id: '172878f5-0c0d-4de9-b7f2-6fb03a9356b3', access: 'roles' },
    ],
  },
  // settings: null,
  providers: [],
};

export const createUserPayload: z.infer<typeof createUserSchema> = {
  email: 'testdudemanbroseph420@qdoc.ca',
  firstName: 'dudetest',
  lastName: 'buddyguy',
  roleID: 'c159e7ff-3c56-495e-be74-d38ef0cfcf93',
  organizationID: '275ea451-85b2-4469-855d-14753c15cca4',
  phone: '+12042222224',
  providerID: null,
};

export const permanentProvider: z.infer<typeof providerSchema> = {
  id: '4b8ba394-821b-4f00-9e55-d1cc78ea7c07',
  title: 'Dr.',
  firstName: 'Permanent',
  lastName: 'Provider',
  email: 'permanent.provider@example.com',
  license: '99999',
  province: 'MB',
  specialtyCode: '111',
  specialtyStartDate: '2024-01-01',
  searchVector: "'rockmus':2 'weezer':1",
  siteID: permanentSite.id,
  userID: permanentUser.id,
  user: permanentUser,
  specialty: {
    province: 'MB',
    code: '111',
    description: 'GENERAL PRACTICE - URBAN',
  },
  licenses: [
    {
      payorID: '7529071b-1c83-4e10-ba1b-9d3cb6a17de2',
      payorName: 'MB',
      license: '00019',
      siteID: permanentSite.id,
    },
  ],
  sites: [permanentSite],
  createdAt: DateTime.now().toJSDate(),
  groups: [],
};

export const integrationProvider: z.infer<typeof providerSchema> = {
  id: 'fab8c0ec-191f-4046-b529-482ee2f098e9',
  title: 'Dr.',
  firstName: 'Integration',
  lastName: 'Provider',
  email: 'brettlungal@gmail.com',
  license: '99998',
  province: 'MB',
  specialtyCode: '111',
  specialtyStartDate: '2024-01-01',
  searchVector: "'rockmus':2 'weezer':1",
  siteID: permanentSite.id,
  userID: null,
  user: null,
  specialty: {
    province: 'MB',
    code: '111',
    description: 'GENERAL PRACTICE - URBAN',
  },
  licenses: [
    {
      payorID: '7529071b-1c83-4e10-ba1b-9d3cb6a17de2',
      payorName: 'MB',
      license: '00019',
      siteID: permanentSite.id,
    },
    {
      payorID: 'eb200898-38be-42de-8c2d-9b3e980bd614',
      payorName: 'WCB',
      license: '12345',
      siteID: permanentSite.id,
    },
    {
      payorID: 'ac30af3f-2a28-4994-8653-d457a833550a',
      payorName: 'Blue Cross',
      license: '54321',
      siteID: permanentSite.id,
    },
    {
      payorID: '7529071b-1c83-4e10-ba1b-9d3cb6a17de2',
      payorName: 'MB',
      license: '00019',
      siteID: permanentSite.id,
    },
  ],
  sites: [permanentSite],
  createdAt: DateTime.now().toJSDate(),
  groups: [],
};

export const permanentPatient: z.infer<typeof patientSchema> = {
  id: '0cd24476-ea44-411f-bded-af38583639c5',
  firstName: 'PermanentFirst',
  lastName: 'PermanentLast',
  dateOfBirth: '2000-01-01',
  address: '100 Permanent Ave',
  city: 'Persist City',
  country: 'Canada',
  postalCode: 'A1A1A1',
  sex: 'M',
  maritalStatus: 'S',
  province: 'MB',
  email: 'fake@email.com',
  phone: '+12045555555',
  searchVector: null,
  organizationID: permanentOrganization.id,
  healthcards: [
    {
      id: '1cd24476-ea44-411f-bded-af38583639c5',
      number: '123456789',
      registration: '123456',
      patientID: '0cd24476-ea44-411f-bded-af38583639c5',
      province: 'MB',
    },
  ],
};

export const permanentIntegration = {
  organization: permanentOrganization,
  site: permanentSite,
  user: permanentUser,
  provider: permanentProvider,
  patient: permanentPatient,
  integrationOrganization,
  integrationSite,
  integrationTestUser,
  createUserPayload,
};
